// components/FormPackage/StepPaquetes.tsx
import { Box, Button, Stack, Typography } from "@mui/material";
import React from "react";
import type { Paquete } from "../../firebase/firestore/paquetes";
import type { Usuario } from "../../firebase/firestore/usuarios";
import { camposFaltantesSegunEstado } from "../../utils/entregaValidations";
import { useAgenciaStore } from "../../zustand/useAgenciaStore";
import PaqueteItem, {
  type PaqueteForm,
  type PaqueteFormValue,
} from "./PackageItem";

type FieldPath = keyof Paquete | "peso.monto" | "peso.unidad";

interface Props {
  paquetes: PaqueteForm[];
  setPaquetes: (paquetes: PaqueteForm[]) => void;
  handleNext: () => void;
  handleBack: () => void;
  activeStep: number;
  usuario?: Usuario;
}

export default function StepPaquetes({
  paquetes,
  setPaquetes,
  handleNext,
  handleBack,
  activeStep,
  usuario,
}: Props) {
  const { agencia } = useAgenciaStore();
  const [touched, setTouched] = React.useState<{
    [index: number]: Partial<Record<FieldPath, boolean>>;
  }>({});

  const handleAgregarPaquete = () => {
    const nuevaVia = "aereo";
    const tarifaInicial = usuario?.tarifaAerea;

    setPaquetes([
      ...paquetes,
      {
        idRastreo: "",
        via: nuevaVia,
        contenido: "",
        ubicacionFisica: "",
        estado: agencia?.estados?.[0]?.id || "recibido",
        tarifa: tarifaInicial,
        peso: {
          monto: 0,
          unidad: "lb",
        },
        cobroAdicional: 0,
        costo: undefined,
        ganancia: undefined,
        detallesEntrega: undefined,
        puntosAcumulados: undefined,
        puntosRedimidos: undefined,
        descuento: undefined,
      },
    ]);
  };

  const handleBlur = (index: number, field: FieldPath) => {
    setTouched((prev) => ({
      ...prev,
      [index]: {
        ...prev[index],
        [field]: true,
      },
    }));
  };

  const handleRemove = (index: number) => {
    setPaquetes(paquetes.filter((_, i) => i !== index));
  };

  const handleChange = (
    index: number,
    field: keyof Paquete,
    value: PaqueteFormValue
  ) => {
    const newPaquetes = [...paquetes];
    const updatedPaquete: PaqueteForm = {
      ...newPaquetes[index],
      [field]: value,
    };

    // Si se cambia la vía y el usuario tiene tarifa predeterminada, usar esa tarifa
    if (field === "via" && usuario && !newPaquetes[index].id) {
      const via = value as "aereo" | "maritimo";
      if (via === "aereo" && usuario.tarifaAerea) {
        updatedPaquete.tarifa = usuario.tarifaAerea;
      } else if (via === "maritimo" && usuario.tarifaMaritima) {
        updatedPaquete.tarifa = usuario.tarifaMaritima;
      }
    }

    newPaquetes[index] = updatedPaquete;
    setPaquetes(newPaquetes);
  };

  const updatingPage = paquetes.some((paquete) => paquete.id);

  // Ningun paquete puede quedar en un estado entregado sin peso ni tarifa.
  const hayEntregasIncompletas = paquetes.some(
    (paquete) =>
      camposFaltantesSegunEstado(agencia, paquete.estado, paquete).length > 0
  );

  return (
    <Box
      sx={{
        minHeight: 300,
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
      }}
    >
      <Box>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Información de los paquetes
        </Typography>
        <Stack spacing={2}>
          {paquetes.map((paquete, index) => (
            <PaqueteItem
              key={paquete.id || `paquete-${index}`}
              index={index}
              paquete={paquete}
              onChange={handleChange}
              onRemove={handleRemove}
              touched={touched[index]}
              onBlur={handleBlur}
              disableRemove={false}
              usuario={usuario}
            />
          ))}
          {!updatingPage && (
            <Button onClick={handleAgregarPaquete} variant="outlined">
              Agregar paquete
            </Button>
          )}
        </Stack>
      </Box>
      <Box sx={{ display: "flex", flexDirection: "row", pt: 2 }}>
        <Button disabled={activeStep === 0} onClick={handleBack} sx={{ mr: 1 }}>
          Anterior
        </Button>
        <Box sx={{ flex: "1 1 auto" }} />
        <Button
          onClick={handleNext}
          disabled={
            !paquetes.every((p) => p.idRastreo?.trim() && p.via?.trim()) ||
            hayEntregasIncompletas
          }
        >
          Siguiente
        </Button>
      </Box>
    </Box>
  );
}
